import { useTranslation } from "react-i18next";
import {
  Footprints,
  ArrowLeftRight,
  BadgeDollarSign,
  Volume2,
  Wallet,
  Waves,
} from "lucide-react";
import useReveal from "src/hooks/use-reveal";
import SectionBackdrop from "src/sections/components/section-backdrop";
import { SECTION_IMAGES } from "src/config/images";

// ЯПОНИЯ ЗА ШЕСТЬ КАРТОЧЕК: ЭТИКЕТ И БЫТ.
//
// Зачем это на сайте турфирмы. Человек, который едет в Японию впервые,
// боится не цены — он боится опозориться: не так снять обувь, не туда встать
// на эскалаторе, оставить чаевые и обидеть официанта. Ни один конкурент об
// этом не говорит: у всех «богатая культура и вековые традиции».
// Шесть коротких карточек с настоящими бытовыми правилами делают то, чего не
// сделает ни один рекламный текст, — показывают, что мы действительно живём
// в этой стране и позаботимся о мелочах.
//
// Содержимое — общеизвестные правила японского быта, а не обещания компании.
// Тексты лежат в переводах (ключ tips.*), карточки — просто вёрстка.
//
// Стекло (glassmorphism) здесь уместно: карточки лежат поверх тёмного фона
// страницы и должны читаться как заметки на кальке, а не как ещё один
// плотный блок.

const TIPS = [
  { id: "shoes", Icon: Footprints },
  { id: "escalator", Icon: ArrowLeftRight },
  { id: "tips", Icon: BadgeDollarSign },
  { id: "quiet", Icon: Volume2 },
  { id: "cash", Icon: Wallet },
  { id: "onsen", Icon: Waves },
];

export default function JapanTips() {
  const { t } = useTranslation();
  const reveal = useReveal();

  return (
    <section
      id="tips"
      ref={reveal.ref}
      className={`relative w-full max-w-[1526px] mx-auto px-4 py-10 sm:py-16 ${reveal.className}`}
    >
      {/* Фото во всю ширину окна — выбирается в src/config/images.js */}
      <SectionBackdrop image={SECTION_IMAGES.tips} />

      <div className="relative flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div>
          <p className="on-photo text-xs sm:text-sm font-extrabold uppercase tracking-[0.28em] text-kj-gold">
            {t("tips.eyebrow")}
          </p>
          <h2 className="on-photo mt-3 text-2xl sm:text-4xl font-extrabold uppercase tracking-tight text-kj-ink text-balance">
            {t("tips.title")}
          </h2>
        </div>
        <p className="on-photo text-sm sm:text-base text-kj-sub leading-relaxed max-w-[46ch]">
          {t("tips.subtitle")}
        </p>
      </div>

      {/* На телефоне — лента со свайпом (карточка занимает почти весь экран,
          край следующей выглядывает). От планшета и выше — сетка. */}
      <ul className="relative tour-rail no-scrollbar mt-7 sm:mt-9 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-5">
        {TIPS.map(({ id, Icon }) => (
          <li
            key={id}
            className="tour-rail-item sm:flex-none group relative overflow-hidden rounded-2xl border border-white/10 bg-kj-dark/45 backdrop-blur-md p-5 sm:p-6 transition-colors duration-300 hover:border-kj-gold/50 hover:bg-kj-dark/60"
          >
            {/* Тёплый отсвет из угла — он же реагирует на наведение */}
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-kj-gold/10 blur-2xl transition-opacity duration-500 opacity-0 group-hover:opacity-100"
            />
            <span className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-kj-gold/40 text-kj-gold">
              <Icon aria-hidden="true" className="h-5 w-5" strokeWidth={1.6} />
            </span>
            <h3 className="relative mt-4 text-base sm:text-lg font-bold text-kj-ink leading-snug">
              {t(`tips.items.${id}.title`)}
            </h3>
            <p className="relative mt-2 text-sm sm:text-[15px] text-kj-sub leading-relaxed">
              {t(`tips.items.${id}.text`)}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}
